import { SafeExternalLink } from "@/components/SafeExternalLink";
import { EditorialImage } from "@/components/EditorialImage";
import { SaveButton } from "@/components/SaveButton";
import { PaywallBadge } from "@/components/PaywallBadge";
import { OutboundArrow } from "@/components/OutboundArrow";
import { normalizeTitle } from "@/lib/normalize-title";
import { TOPIC_LABEL, type Topic } from "@/lib/topics";
import { getMotifFallback } from "@/lib/motif-fallback";
import type { RecipeRow, VideoRow } from "@/lib/queries";

const CATEGORY_LABEL: Record<string, string> = {
  groent: "Grønt",
  koed_fisk: "Kød & fisk",
  bagvaerk_broed: "Bagværk & brød",
  soede_sager: "Søde sager",
  drikke: "Drikke",
  basis: "Basis",
};

const TRADITION_LABEL: Record<string, string> = {
  dansk: "Dansk",
  nordisk: "Nordisk",
  italiensk: "Italiensk",
  fransk: "Fransk",
  spansk: "Spansk",
  mellemoestlig: "Mellemøstlig",
  nordafrikansk: "Nordafrikansk",
  japansk: "Japansk",
  kinesisk: "Kinesisk",
  sydoestasiatisk: "Sydøstasiatisk",
  indisk: "Indisk",
  mexicansk: "Mexicansk",
  americansk: "Amerikansk",
  joedisk: "Jødisk",
  andet: "Andet",
};

function formatDuration(s: number | null): string | null {
  if (!s || s <= 0) return null;
  const m = Math.floor(s / 60);
  const sec = s % 60;
  if (m >= 60) {
    const h = Math.floor(m / 60);
    const rem = m % 60;
    return `${h} t ${rem} min`;
  }
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

function recipeKicker(recipe: RecipeRow) {
  const parts: string[] = ["Seneste opskrift"];
  if (recipe.category && CATEGORY_LABEL[recipe.category]) {
    parts.push(CATEGORY_LABEL[recipe.category]);
  }
  if (recipe.tradition && recipe.tradition !== "andet" && TRADITION_LABEL[recipe.tradition]) {
    parts.push(TRADITION_LABEL[recipe.tradition]);
  }
  return parts.join(" · ");
}

function videoKicker(video: VideoRow) {
  const provider = video.provider === "youtube" ? "YouTube" : "Vimeo";
  return `Seneste video · ${TOPIC_LABEL[video.topic as Topic] ?? "Video"} · ${provider}`;
}

export function LatestMediaSection({
  recipe,
  video,
}: {
  recipe: RecipeRow | null;
  video: VideoRow | null;
}) {
  if (!recipe && !video) return null;

  return (
    <section className="rule-double py-12 md:py-[52px]" aria-label="Seneste opskrift og video">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-6 md:mb-7">
        <h2 className="font-serif font-normal text-[26px] md:text-[32px] m-0 text-ink">
          Fra køkkenet &amp; skærmen
        </h2>
        <span className="font-serif italic text-[13px] md:text-[15px] text-muted">
          ét fund ad gangen
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12">
        {recipe && (
          <article className="flex flex-col">
            <SafeExternalLink
              href={recipe.original_url ?? "#"}
              className="fl-card group flex flex-col"
            >
              <span className="font-serif text-[11px] font-medium tracking-[0.18em] uppercase text-accent block mb-3">
                {recipeKicker(recipe)}
                <span className="ml-2">
                  <PaywallBadge paywall={recipe.paywall} />
                </span>
              </span>
              <p className="meta italic text-ink-soft mb-3">
                Én ret om dagen. Sæson og råvare før teknik og prestige.
              </p>
              <EditorialImage
                src={recipe.image_url}
                alt={recipe.title}
                ratio="16/9"
                className="mb-4"
                fallback={getMotifFallback("recipe", recipe.category ?? "mad")}
              />
              <h3 className="font-serif font-normal text-[22px] md:text-[24px] leading-[1.22] text-ink group-hover:text-accent transition-colors">
                {normalizeTitle(recipe.title)}
              </h3>
              {(recipe.chef || recipe.source) && (
                <p className="mt-2 font-serif italic text-[13px] text-muted">
                  {recipe.chef ?? recipe.source}
                  {recipe.chef && recipe.source && recipe.chef !== recipe.source
                    ? ` · ${recipe.source}`
                    : ""}
                </p>
              )}
              {recipe.summary && (
                <p className="mt-3 font-serif font-light text-[14px] md:text-[15px] leading-[1.55] text-ink-soft">
                  {recipe.summary}
                </p>
              )}
            </SafeExternalLink>
            <div className="mt-3 flex justify-start">
              <SaveButton id={recipe.id} kind="recipe" compact />
            </div>
          </article>
        )}

        {video && (
          <article className="flex flex-col">
            <SafeExternalLink
              href={video.url}
              className="fl-card group flex flex-col"
            >
              <span className="font-serif text-[11px] font-medium tracking-[0.18em] uppercase text-accent block mb-3">
                {videoKicker(video)}
              </span>
              <p className="meta italic text-ink-soft mb-3">
                Portrætter, dokumentarer og samtaler – til den langsomme opmærksomhed.
              </p>
              <EditorialImage
                src={video.thumbnail_url}
                alt={video.title}
                ratio="16/9"
                play
                duration={formatDuration(video.duration_seconds) ?? undefined}
                className="mb-4"
                fallback={getMotifFallback("video", video.topic)}
              />
              <h3 className="font-serif font-normal text-[22px] md:text-[24px] leading-[1.22] text-ink group-hover:text-accent transition-colors">
                {normalizeTitle(video.title)}
              </h3>
              {(video.channel || formatDuration(video.duration_seconds)) && (
                <p className="mt-2 font-serif italic text-[13px] text-muted">
                  {video.channel ?? ""}
                  {video.channel && formatDuration(video.duration_seconds) ? " · " : ""}
                  {formatDuration(video.duration_seconds)}
                </p>
              )}
              {video.description && (
                <p className="mt-3 font-serif font-light text-[14px] md:text-[15px] leading-[1.55] text-ink-soft">
                  {video.description}
                </p>
              )}
            </SafeExternalLink>
            <div className="mt-3 flex justify-start">
              <SaveButton id={video.id} kind="video" compact />
            </div>
          </article>
        )}
      </div>
    </section>
  );
}
